import React from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import Modal from 'react-bootstrap4-modal';
import { I18n } from '../../../../helpers/i18n';
import { updateMvpUserType, updateMvpUserRoleStatus, getBuddyYouthCommunity, fetchUserMvp } from 'redux/actions/user.action';
import roleExplorerIcon from 'static/images/mvp/role_explorer.png';
import roleGuideIcon from 'static/images/mvp/role_guide.png';
import infoIcon from 'static/images/mvp/Info.png';
import pauseRoleImage from 'static/images/mvp/forget-password.png';
import RoleStateCard from './RoleStateCard';
import '../../../mvpAuth/signin.scss';

const SENIOR = 'explorer';
const BUDDY = 'guide';
const VERIFIED = 'verified';
const GUIDE_STATUS_ACTIVE = 'active';
const GUIDE_STATUS_INACTIVE = 'inactive';

const TURN_BUTTON_STYLE = {
  actionBackground: '#F8F1EA',
  actionTextColor: '#E87623',
  secondaryActionBackground: '#F8F1EA',
  secondaryActionTextColor: '#E87623',
};

function getDisabledBadgeStyle() {
  return {
    statusLabel: I18n.t('role_card_status_disabled'),
    badgeBackground: TURN_BUTTON_STYLE.actionBackground,
    badgeTextColor: TURN_BUTTON_STYLE.actionTextColor,
  };
}

const LIGHT_CARD_SURFACE = {
  cardBackground: '#F9FAFB',
  cardBorderColor: '#EEEFF2',
  titleColor: '#111827',
  descriptionColor: '#111827',
};

const ACTIVE_CARD_SURFACE = {
  cardBackground: '#699CF4',
  cardBorderColor: 'transparent',
  titleColor: '#FFFFFF',
  descriptionColor: '#FFFFFF',
};

const ROLE_STATUS_TOAST_OPTIONS = {
  position: 'top-center',
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: false,
  draggable: false,
  progress: undefined,
  theme: 'colored',
  className: 'toast-success-container toast-success-container-after',
};

function getRoleStatusSuccessMessage(action) {
  if (action === 'turn_off') {
    return (
      <div>
        <div>{I18n.t('home_guide_time_off_banner_title')}</div>
        <div>{I18n.t('guide_time_off_toast_subtitle')}</div>
      </div>
    );
  }
  return I18n.t('role_updated_successfully');
}

/** @returns {'explorer'|'guide'|''} */
function normalizeMvpUserType(mvpUserType, userType) {
  if (mvpUserType === SENIOR || mvpUserType === BUDDY) {
    return mvpUserType;
  }
  if (mvpUserType === 'both') {
    if (userType === SENIOR || userType === BUDDY) {
      return userType;
    }
    return SENIOR;
  }
  return mvpUserType || '';
}

function extractYouthCommunityRaw(youthCommunity) {
  if (!youthCommunity) return null;
  if (typeof youthCommunity === 'string') return youthCommunity;
  if (typeof youthCommunity === 'object') {
    return youthCommunity._id || youthCommunity.value || null;
  }
  return null;
}

function resolveYouthCommunityId(raw, communities) {
  if (!raw || raw === 'N/A') return null;
  if (!communities || communities.length === 0) return raw;
  const foundById = communities.find((c) => c._id === raw);
  if (foundById) return foundById._id;
  const foundByValue = communities.find((c) => c.value === raw);
  if (foundByValue) return foundByValue._id;
  return raw;
}

function getSwitchableCardState(statusVerify) {
  return statusVerify === VERIFIED ? 'switchableQualified' : 'switchable';
}

function getExplorerCardState(activeType, statusVerify) {
  // Guide (or was guide): explorer is always registered/switchable, never inactive
  if (activeType === BUDDY) return getSwitchableCardState(statusVerify);
  if (activeType !== SENIOR) return 'inactive';
  if (statusVerify === VERIFIED) return 'qualified';
  return 'registered';
}

function normalizeGuideStatus(guideStatus) {
  if (guideStatus === GUIDE_STATUS_ACTIVE || guideStatus === GUIDE_STATUS_INACTIVE) {
    return guideStatus;
  }
  return GUIDE_STATUS_ACTIVE;
}

function getGuideCardState(activeType, statusVerify, canSwitchToGuide, guideStatus) {
  const normalizedGuideStatus = normalizeGuideStatus(guideStatus);

  if (normalizedGuideStatus === GUIDE_STATUS_INACTIVE && canSwitchToGuide) {
    return getSwitchableCardState(statusVerify);
  }
  if (activeType === SENIOR && canSwitchToGuide) return getSwitchableCardState(statusVerify);
  if (activeType !== BUDDY) return 'inactive';
  if (statusVerify === VERIFIED) return 'qualified';
  return 'registered';
}

function applyGuideTurnButtonConfig(config, guideCardState, guideStatus, hasYouthCommunity, handlers, statusVerify, activeType) {
  if (!config || guideCardState === 'inactive' || !hasYouthCommunity) {
    return config;
  }

  if (statusVerify !== VERIFIED) {
    return config;
  }

  const normalizedGuideStatus = normalizeGuideStatus(guideStatus);
  const { onTurnOn, onTurnOff } = handlers;

  if (normalizedGuideStatus === GUIDE_STATUS_INACTIVE) {
    if (
      activeType === BUDDY &&
      (guideCardState === 'switchable' || guideCardState === 'switchableQualified')
    ) {
      return {
        ...config,
        ...getDisabledBadgeStyle(),
        actionLabel: I18n.t('role_card_cta_turn_on'),
        showAction: true,
        onActionClick: onTurnOn,
        showSecondaryAction: false,
        secondaryActionLabel: undefined,
        onSecondaryActionClick: null,
        ...ACTIVE_CARD_SURFACE,
        ...TURN_BUTTON_STYLE,
      };
    }
    return config;
  }

  if (guideCardState === 'qualified') {
    return {
      ...config,
      actionLabel: I18n.t('role_card_cta_turn_off'),
      showAction: true,
      onActionClick: onTurnOff,
      showSecondaryAction: false,
      onSecondaryActionClick: null,
      ...TURN_BUTTON_STYLE,
    };
  }

  if (guideCardState === 'registered') {
    return {
      ...config,
      showSecondaryAction: true,
      secondaryActionLabel: I18n.t('role_card_cta_turn_off'),
      onSecondaryActionClick: onTurnOff,
      secondaryActionBackground: TURN_BUTTON_STYLE.secondaryActionBackground,
      secondaryActionTextColor: TURN_BUTTON_STYLE.secondaryActionTextColor,
    };
  }

  return config;
}

class RoleSelection extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedType: normalizeMvpUserType(props.user?.mvpUserType, props.user?.type),
      listBuddyYouthCommunity: [],
      youthCommunity: '',
      isLoading: false,
      showGuideModal: false,
      showDisableGuideModal: false,
      modalYouthCommunity: '',
    };
    this.youthCommunitySelectRef = React.createRef();
    this.handleLanguageChanged = this.handleLanguageChanged.bind(this);
  }

  handleLanguageChanged = () => {
    this.forceUpdate();
  };

  async componentDidMount() {
    I18n.on('languageChanged', this.handleLanguageChanged);

    const { getBuddyYouthCommunity, user } = this.props;
    this.setState({ selectedType: normalizeMvpUserType(user?.mvpUserType, user?.type) });
    
    // Fetch youth communities with forSignup to get Individual user, None of the above + DB communities (no N/A)
    const communities = await getBuddyYouthCommunity({ forSignup: true });
    if (communities) {
      // Always try to load youthCommunity from user props (even if user is currently explorer)
      // This allows pre-loading when user switches back to Guide
      let selectedYouthCommunity = '';
      if (user?.youthCommunity) {
        // Extract the actual ID/value from user.youthCommunity
        // It could be: string (_id), string (value), or object {value, label, _id}
        let youthCommunityToFind = null;
        if (typeof user.youthCommunity === 'string') {
          youthCommunityToFind = user.youthCommunity;
        } else if (user.youthCommunity && typeof user.youthCommunity === 'object') {
          // It's an object, try _id first, then value
          youthCommunityToFind = user.youthCommunity._id || user.youthCommunity.value;
        }
        
        if (youthCommunityToFind) {
          // Try to find by _id first (new format)
          const foundById = communities.find(c => c._id === youthCommunityToFind);
          if (foundById) {
            selectedYouthCommunity = foundById._id;
          } else {
            // Fallback: try to find by value (old format for backward compatibility)
            const foundByValue = communities.find(c => c.value === youthCommunityToFind);
            if (foundByValue) {
              selectedYouthCommunity = foundByValue._id;
            }
          }
        }
      }
      
      this.setState({ 
        listBuddyYouthCommunity: communities,
        youthCommunity: selectedYouthCommunity
      });
    }
  }

  componentWillUnmount() {
    I18n.off('languageChanged', this.handleLanguageChanged);
  }

  componentDidUpdate(prevProps) {
    const { user } = this.props;
    const { listBuddyYouthCommunity, showGuideModal } = this.state;
    
    // Update selectedType when mvpUserType changes
    if (prevProps.user?.mvpUserType !== user?.mvpUserType || prevProps.user?.type !== user?.type) {
      this.setState({ selectedType: normalizeMvpUserType(user?.mvpUserType, user?.type) });
    }
    
    // Update youthCommunity whenever user props change (especially after fetchUserMvp)
    // Also update modalYouthCommunity if modal is currently showing
    if (user?.youthCommunity && listBuddyYouthCommunity.length > 0) {
      // Extract the actual ID/value from user.youthCommunity
      // It could be: string (_id), string (value), or object {value, label, _id}
      let youthCommunityToFind = null;
      if (typeof user.youthCommunity === 'string') {
        youthCommunityToFind = user.youthCommunity;
      } else if (user.youthCommunity && typeof user.youthCommunity === 'object') {
        // It's an object, try _id first, then value
        youthCommunityToFind = user.youthCommunity._id || user.youthCommunity.value;
      }
      
      if (youthCommunityToFind) {
        // Try to find by _id first
        const foundById = listBuddyYouthCommunity.find(c => c._id === youthCommunityToFind);
        if (foundById) {
          const updates = {};
          // Only update youthCommunity if different
          if (this.state.youthCommunity !== foundById._id) {
            updates.youthCommunity = foundById._id;
          }
          // Update modalYouthCommunity if modal is showing and value is different
          if (showGuideModal && this.state.modalYouthCommunity !== foundById._id) {
            updates.modalYouthCommunity = foundById._id;
          }
          if (Object.keys(updates).length > 0) {
            this.setState(updates);
          }
        } else {
          // Fallback: try to find by value
          const foundByValue = listBuddyYouthCommunity.find(c => c.value === youthCommunityToFind);
          if (foundByValue) {
            const updates = {};
            // Only update youthCommunity if different
            if (this.state.youthCommunity !== foundByValue._id) {
              updates.youthCommunity = foundByValue._id;
            }
            // Update modalYouthCommunity if modal is showing and value is different
            if (showGuideModal && this.state.modalYouthCommunity !== foundByValue._id) {
              updates.modalYouthCommunity = foundByValue._id;
            }
            if (Object.keys(updates).length > 0) {
              this.setState(updates);
            }
          }
        }
      }
    }
  }

  hasYouthCommunity = () => {
    const { user } = this.props;
    const { youthCommunity, listBuddyYouthCommunity } = this.state;
    const fromUser = resolveYouthCommunityId(
      extractYouthCommunityRaw(user?.youthCommunity),
      listBuddyYouthCommunity,
    );
    const fromState = resolveYouthCommunityId(youthCommunity, listBuddyYouthCommunity);
    return !!(fromUser || fromState);
  };

  getYouthCommunityIdForApi = () => {
    const { user } = this.props;
    const { youthCommunity, listBuddyYouthCommunity } = this.state;
    return (
      resolveYouthCommunityId(youthCommunity, listBuddyYouthCommunity) ||
      resolveYouthCommunityId(extractYouthCommunityRaw(user?.youthCommunity), listBuddyYouthCommunity)
    );
  };

  getModalPreselectedYouthCommunity = () => {
    const { user } = this.props;
    const { listBuddyYouthCommunity } = this.state;
    const raw = extractYouthCommunityRaw(user?.youthCommunity);
    if (!raw || listBuddyYouthCommunity.length === 0) {
      return '';
    }
    return resolveYouthCommunityId(raw, listBuddyYouthCommunity) || '';
  };

  openGuideModal = () => {
    this.setState({
      showGuideModal: true,
      modalYouthCommunity: this.getModalPreselectedYouthCommunity(),
    });
  };

  handleSwitchToExplorer = () => {
    this.updateRole(SENIOR);
  };

  handleSwitchOrBecomeGuide = () => {
    if (this.hasYouthCommunity()) {
      this.updateRole(BUDDY);
      return;
    }
    this.openGuideModal();
  };

  updateRoleStatus = async (action) => {
    if (this.state.isLoading) return;

    const role = BUDDY;
    let youthCommunityId = null;
    if (action === 'turn_on') {
      youthCommunityId = this.getYouthCommunityIdForApi();
      if (!youthCommunityId) {
        toast.error(I18n.t('please_select_youth_community'), {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          theme: 'colored',
          className: 'toast-error-container toast-error-container-after',
        });
        return;
      }
    }

    this.setState({ isLoading: true });

    try {
      const { updateMvpUserRoleStatus, fetchUserMvp } = this.props;

      await updateMvpUserRoleStatus(action, role, youthCommunityId);

      await fetchUserMvp();

      toast.success(getRoleStatusSuccessMessage(action), ROLE_STATUS_TOAST_OPTIONS);
    } catch (err) {
      toast.error(err.message || I18n.t('something_wrong'), {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        theme: 'colored',
        className: 'toast-error-container toast-error-container-after',
      });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleTurnOn = () => {
    this.updateRoleStatus('turn_on');
  };

  handleTurnOff = () => {
    this.setState({ showDisableGuideModal: true });
  };

  closeDisableGuideModal = () => {
    this.setState({ showDisableGuideModal: false });
  };

  confirmDisableGuide = () => {
    this.setState({ showDisableGuideModal: false }, () => {
      this.updateRoleStatus('turn_off');
    });
  };

  handleSelectCommunity = (data) => {
    this.setState({ youthCommunity: data });
  };

  handleModalSelectCommunity = (data) => {
    // Ensure we store the value correctly
    // data could be: string (_id), string (value), or empty string
    const valueToStore = data && data.trim ? data.trim() : data;
    this.setState({ modalYouthCommunity: valueToStore });
  };

  closeGuideModal = () => {
    this.setState({ 
      showGuideModal: false,
      modalYouthCommunity: '',
    });
  };

  handleBecomeGuide = async () => {
    const { modalYouthCommunity, listBuddyYouthCommunity } = this.state;
    
    // Try to get value from select element directly first (in case state hasn't updated)
    // This ensures we get the current selected value even if state update is pending
    let selectedValue = null;
    try {
      // Try to use ref first, then fall back to DOM query
      const selectElement = this.youthCommunitySelectRef.current || 
                           document.querySelector('.become-guide-modal select');
      if (selectElement && selectElement.value && selectElement.value !== '') {
        selectedValue = selectElement.value;
        // Update state immediately to keep it in sync
        if (selectedValue !== modalYouthCommunity) {
          this.setState({ modalYouthCommunity: selectedValue });
        }
      }
    } catch (e) {
      // If we can't access DOM, fall back to state
      console.warn('Could not access select element:', e);
    }
    
    // If we didn't get value from DOM, use state
    if (!selectedValue) {
      // Extract the actual value from modalYouthCommunity
      // It could be: string (_id), string (value), or empty string
      selectedValue = modalYouthCommunity;
      if (typeof modalYouthCommunity === 'object' && modalYouthCommunity !== null) {
        selectedValue = modalYouthCommunity._id || modalYouthCommunity.value || '';
      } else if (typeof modalYouthCommunity === 'string') {
        selectedValue = modalYouthCommunity.trim();
      }
    }
    
    // Validate that a youth community is selected
    if (!selectedValue || selectedValue === '' || selectedValue === 'null' || selectedValue === 'undefined') {
      toast.error(I18n.t('please_select_youth_community'), {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        theme: 'colored',
        className: 'toast-error-container toast-error-container-after',
      });
      return;
    }
    
    // Update state with selected youth community
    this.setState({ 
      youthCommunity: selectedValue,
      modalYouthCommunity: selectedValue, // Ensure state is also updated
      showGuideModal: false,
    });
    
    // Use _id of youthCommunity for API
    let youthCommunityId = null;
    if (selectedValue && listBuddyYouthCommunity.length > 0) {
      // Try to find by _id first
      const foundById = listBuddyYouthCommunity.find(item => 
        item._id === selectedValue || 
        (item._id && item._id.toString() === selectedValue.toString())
      );
      if (foundById) {
        youthCommunityId = foundById._id;
      } else {
        // Fallback: try to find by value (backward compatibility)
        const foundByValue = listBuddyYouthCommunity.find(item => 
          item.value === selectedValue || 
          (item.value && item.value.toString() === selectedValue.toString())
        );
        if (foundByValue) {
          youthCommunityId = foundByValue._id;
        } else {
          // Try to find by label (in case value is the label text)
          const foundByLabel = listBuddyYouthCommunity.find(item => 
            item.label === selectedValue || 
            (item.label && item.label.toString() === selectedValue.toString())
          );
          if (foundByLabel) {
            youthCommunityId = foundByLabel._id;
          } else {
            // If not found, use the value directly (might be an ObjectId string)
            youthCommunityId = selectedValue;
          }
        }
      }
    } else if (selectedValue) {
      // If list is empty but we have a value, use it directly
      youthCommunityId = selectedValue;
    }
    
    await this.updateRoleWithYouthCommunity(BUDDY, youthCommunityId);
  };

  updateRoleWithYouthCommunity = async (newType, youthCommunityId) => {
    if (this.state.isLoading) return;
    
    this.setState({ isLoading: true });
    
    try {
      const { updateMvpUserType, fetchUserMvp, user } = this.props;
      
      await updateMvpUserType(newType, youthCommunityId);
      
      // Ensure user data is refreshed and localStorage is updated
      // updateMvpUserType already calls fetchUserMvp, but we call it again to ensure
      // all components get the latest data
      await fetchUserMvp((updatedUser) => {
        if (updatedUser) {
          // User data has been updated in Redux and localStorage
          // This ensures all components using user data get the latest info
        }
      });
      
      this.setState({
        selectedType: normalizeMvpUserType(newType, this.props.user?.type),
      });
      
      toast.success(I18n.t('role_updated_successfully'), {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        theme: 'colored',
        className: 'toast-success-container toast-success-container-after',
      });
    } catch (err) {
      toast.error(err.message || I18n.t('something_wrong'), {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        theme: 'colored',
        className: 'toast-error-container toast-error-container-after',
      });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  updateRole = async (newType) => {
    if (this.state.isLoading) return;
    
    this.setState({ isLoading: true });
    
    try {
      const { updateMvpUserType, fetchUserMvp, user } = this.props;
      
      let youthCommunityId = null;
      if (newType === BUDDY) {
        youthCommunityId = this.getYouthCommunityIdForApi();
        if (!youthCommunityId) {
          toast.error(I18n.t('please_select_youth_community'), {
            position: 'top-center',
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: 'colored',
            className: 'toast-error-container toast-error-container-after',
          });
          this.setState({ isLoading: false });
          return;
        }
      }

      const roleToSend = newType === SENIOR || newType === BUDDY ? newType : normalizeMvpUserType(user?.mvpUserType, user?.type);
      await updateMvpUserType(roleToSend, youthCommunityId);
      
      // Ensure user data is refreshed and localStorage is updated
      // updateMvpUserType already calls fetchUserMvp, but we call it again to ensure
      // all components get the latest data
      await fetchUserMvp((updatedUser) => {
        if (updatedUser) {
          // User data has been updated in Redux and localStorage
          // This ensures all components using user data get the latest info
        }
      });
      
      this.setState({ selectedType: normalizeMvpUserType(newType || user?.mvpUserType, user?.type) });
      
      toast.success(I18n.t('role_updated_successfully'), {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        theme: 'colored',
        className: 'toast-success-container toast-success-container-after',
      });
    } catch (err) {
      toast.error(err.message || I18n.t('something_wrong'), {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        theme: 'colored',
        className: 'toast-error-container toast-error-container-after',
      });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  render() {
    const { user } = this.props;
    const { selectedType, isLoading } = this.state;
    const isSenior = selectedType === SENIOR;
    const isBuddy = selectedType === BUDDY;
    const hasYouthCommunity = this.hasYouthCommunity();
    const explorerCardState = getExplorerCardState(selectedType, user?.statusVerify);
    const guideCardState = getGuideCardState(
      selectedType,
      user?.statusVerify,
      hasYouthCommunity,
      user?.guideStatus,
    );
    const explorerCardConfig = {
      inactive: {
        statusLabel: I18n.t('role_card_inactive'),
        description: I18n.t('role_explorer_card_desc_inactive'),
        actionLabel: I18n.t('role_explorer_card_cta_become'),
        showAction: true,
        onActionClick: () => this.handleSwitchToExplorer(),
        ...LIGHT_CARD_SURFACE,
        badgeBackground: '#EEEFF2',
        badgeTextColor: '#111827',
        actionBackground: '#E87623',
        actionTextColor: '#FFFFFF',
      },
      switchable: {
        statusLabel: I18n.t('role_card_registered'),
        description: I18n.t('role_explorer_card_desc_switchable'),
        actionLabel: I18n.t('role_explorer_card_cta_switch'),
        showAction: true,
        onActionClick: () => this.handleSwitchToExplorer(),
        ...LIGHT_CARD_SURFACE,
        badgeBackground: '#DDF8E7',
        badgeTextColor: '#11A75D',
        actionBackground: '#E87623',
        actionTextColor: '#FFFFFF',
      },
      switchableQualified: {
        statusLabel: I18n.t('role_card_qualified'),
        description: I18n.t('role_explorer_card_desc_off'),
        actionLabel: I18n.t('role_explorer_card_cta_switch'),
        showAction: true,
        onActionClick: () => this.handleSwitchToExplorer(),
        ...LIGHT_CARD_SURFACE,
        badgeBackground: '#DDF8E7',
        badgeTextColor: '#11A75D',
        actionBackground: '#E87623',
        actionTextColor: '#FFFFFF',
      },
      registered: {
        statusLabel: I18n.t('role_card_registered'),
        description: I18n.t('role_explorer_card_desc_registered'),
        actionLabel: I18n.t('role_explorer_card_cta_verify_identity'),
        showAction: true,
        onActionClick: () => {
          if (this.props.history && this.props.history.push) {
            this.props.history.push('/verification');
          } else {
            window.location.assign('/verification');
          }
        },
        ...ACTIVE_CARD_SURFACE,
        badgeBackground: '#DDF8E7',
        badgeTextColor: '#11A75D',
        actionBackground: '#F8F1EA',
        actionTextColor: '#E87623',
      },
      qualified: {
        statusLabel: I18n.t('role_card_qualified'),
        description: I18n.t('role_explorer_card_desc_qualified'),
        actionLabel: '',
        showAction: false,
        onActionClick: null,
        ...ACTIVE_CARD_SURFACE,
        badgeBackground: '#DDF8E7',
        badgeTextColor: '#11A75D',
        actionBackground: '#E87623',
        actionTextColor: '#FFFFFF',
      },
    }[explorerCardState];
    const guideCardConfig = {
      inactive: {
        statusLabel: I18n.t('role_card_inactive'),
        description: I18n.t('role_guide_card_desc_inactive'),
        actionLabel: I18n.t('role_guide_card_cta_become'),
        showAction: true,
        onActionClick: () => this.handleSwitchOrBecomeGuide(),
        ...LIGHT_CARD_SURFACE,
        badgeBackground: '#EEEFF2',
        badgeTextColor: '#111827',
        actionBackground: '#E87623',
        actionTextColor: '#FFFFFF',
      },
      switchable: {
        statusLabel: I18n.t('role_card_registered'),
        description: I18n.t('role_guide_card_desc_switchable'),
        actionLabel: I18n.t('role_guide_card_cta_switch'),
        showAction: true,
        onActionClick: () => this.handleSwitchOrBecomeGuide(),
        ...LIGHT_CARD_SURFACE,
        badgeBackground: '#DDF8E7',
        badgeTextColor: '#11A75D',
        actionBackground: '#E87623',
        actionTextColor: '#FFFFFF',
      },
      switchableQualified: {
        statusLabel: I18n.t('role_card_qualified'),
        description: I18n.t('role_guide_card_desc_off'),
        actionLabel: I18n.t('role_guide_card_cta_switch'),
        showAction: true,
        onActionClick: () => this.handleSwitchOrBecomeGuide(),
        ...LIGHT_CARD_SURFACE,
        badgeBackground: '#DDF8E7',
        badgeTextColor: '#11A75D',
        actionBackground: '#E87623',
        actionTextColor: '#FFFFFF',
      },
      registered: {
        statusLabel: I18n.t('role_card_registered'),
        description: I18n.t('role_guide_card_desc_registered'),
        actionLabel: I18n.t('role_guide_card_cta_verify_identity'),
        showAction: true,
        onActionClick: () => {
          if (this.props.history && this.props.history.push) {
            this.props.history.push('/verification');
          } else {
            window.location.assign('/verification');
          }
        },
        showSecondaryAction: false,
        ...ACTIVE_CARD_SURFACE,
        badgeBackground: '#DDF8E7',
        badgeTextColor: '#11A75D',
        actionBackground: '#F8F1EA',
        actionTextColor: '#E87623',
      },
      qualified: {
        statusLabel: I18n.t('role_card_qualified'),
        description: I18n.t('role_guide_card_desc_qualified'),
        actionLabel: '',
        showAction: false,
        onActionClick: null,
        showSecondaryAction: false,
        onSecondaryActionClick: null,
        ...ACTIVE_CARD_SURFACE,
        badgeBackground: '#DDF8E7',
        badgeTextColor: '#11A75D',
        actionBackground: '#E87623',
        actionTextColor: '#FFFFFF',
      },
    }[guideCardState];
    const resolvedGuideCardConfig = applyGuideTurnButtonConfig(
      guideCardConfig,
      guideCardState,
      user?.guideStatus,
      hasYouthCommunity,
      {
        onTurnOn: () => this.handleTurnOn(),
        onTurnOff: () => this.handleTurnOff(),
      },
      user?.statusVerify,
      selectedType,
    );

    return (
      <div className="px-0 user-role-selection" style={{ padding: '0' }}>
        <h3 className="title mt-3 mb-4" style={{ color: '#FFFFFF' }}>
          {I18n.t('role')}
        </h3>
        
        <div style={{ 
          color: '#FFFFFF', 
          borderRadius: '12px', 
          padding: '32px',
          marginBottom: '24px'
        }}>
          
          {/* Two boxes side by side */}
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            flexWrap: 'wrap',
            marginBottom: '24px'
          }}>
            {/* Explorer Box */}
            <RoleStateCard
              className={`card-sign-up senior ${isSenior ? 'checked' : ''}`}
              title={I18n.t('account_type_selection_senior')}
              titleInfoIconSrc={infoIcon}
              titleInfoTooltip={I18n.t('role_explorer_card_title_tooltip')}
              badgeLabel={explorerCardConfig.statusLabel}
              description={explorerCardConfig.description}
              actionLabel={explorerCardConfig.actionLabel}
              onActionClick={explorerCardConfig.onActionClick}
              showAction={explorerCardConfig.showAction}
              secondaryActionLabel={explorerCardConfig.secondaryActionLabel}
              onSecondaryActionClick={explorerCardConfig.onSecondaryActionClick}
              showSecondaryAction={explorerCardConfig.showSecondaryAction}
              secondaryActionBackground={explorerCardConfig.secondaryActionBackground}
              secondaryActionTextColor={explorerCardConfig.secondaryActionTextColor}
              disabled={isLoading}
              cardBackground={explorerCardConfig.cardBackground}
              cardBorderColor={explorerCardConfig.cardBorderColor}
              titleColor={explorerCardConfig.titleColor}
              descriptionColor={explorerCardConfig.descriptionColor}
              badgeBackground={explorerCardConfig.badgeBackground}
              badgeTextColor={explorerCardConfig.badgeTextColor}
              actionBackground={explorerCardConfig.actionBackground}
              actionTextColor={explorerCardConfig.actionTextColor}
              iconSrc={roleExplorerIcon}
              dataTestId={`explorer-card-${explorerCardState}`}
            />

            {/* Guide Box */}
            <RoleStateCard
              className={`card-sign-up buddy ${isBuddy ? 'checked' : ''}`}
              title={I18n.t('account_type_selection_senior_buddy')}
              titleInfoIconSrc={infoIcon}
              titleInfoTooltip={I18n.t('role_guide_card_title_tooltip')}
              badgeLabel={resolvedGuideCardConfig.statusLabel}
              description={resolvedGuideCardConfig.description}
              actionLabel={resolvedGuideCardConfig.actionLabel}
              onActionClick={resolvedGuideCardConfig.onActionClick}
              showAction={resolvedGuideCardConfig.showAction}
              secondaryActionLabel={resolvedGuideCardConfig.secondaryActionLabel}
              onSecondaryActionClick={resolvedGuideCardConfig.onSecondaryActionClick}
              showSecondaryAction={resolvedGuideCardConfig.showSecondaryAction}
              secondaryActionBackground={resolvedGuideCardConfig.secondaryActionBackground}
              secondaryActionTextColor={resolvedGuideCardConfig.secondaryActionTextColor}
              disabled={isLoading}
              cardBackground={resolvedGuideCardConfig.cardBackground}
              cardBorderColor={resolvedGuideCardConfig.cardBorderColor}
              titleColor={resolvedGuideCardConfig.titleColor}
              descriptionColor={resolvedGuideCardConfig.descriptionColor}
              badgeBackground={resolvedGuideCardConfig.badgeBackground}
              badgeTextColor={resolvedGuideCardConfig.badgeTextColor}
              actionBackground={resolvedGuideCardConfig.actionBackground}
              actionTextColor={resolvedGuideCardConfig.actionTextColor}
              iconSrc={roleGuideIcon}
              dataTestId={`guide-card-${guideCardState}`}
            />
          </div>

          {/* Youth Community Selection - Removed: Only used in modal when activating Guide role */}
        </div>

        {/* Become Guide Modal */}
        <style>{`
          .become-guide-modal .modal-content {
            background-color: transparent;
            border: none;
            box-shadow: none;
          }
        `}</style>
        <Modal
          className="become-guide-modal"
          visible={this.state.showGuideModal}
          dialogClassName="modal-dialog-centered"
          onClickBackdrop={this.closeGuideModal}
        >
          <div className="modal-body" style={{
            padding: '40px',
            backgroundColor: '#1F2937',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '100%'
          }}>
            {/* Title */}
            <h4 style={{ 
              color: '#FFFFFF', 
              fontSize: '24px', 
              fontWeight: '600', 
              marginBottom: '16px',
              lineHeight: '1.4',
              textAlign: 'center'
            }}>
              {I18n.t('ready_to_become_guide_title')}
            </h4>

            {/* Description */}
            <p style={{ 
              color: '#fff', 
              fontSize: '16px', 
              lineHeight: '1.5', 
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              {I18n.t('ready_to_become_guide_description')}
            </p>

            {/* Benefits List */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: '12px',
                color: '#FFFFFF'
              }}>
                <span style={{ fontSize: '20px', marginRight: '12px' }}>💰</span>
                <span style={{ fontSize: '16px', lineHeight: '1.5' }}>
                  {I18n.t('ready_to_become_guide_benefit_1')}
                </span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: '12px',
                color: '#FFFFFF'
              }}>
                <span style={{ fontSize: '20px', marginRight: '12px' }}>🤝</span>
                <span style={{ fontSize: '16px', lineHeight: '1.5' }}>
                  {I18n.t('ready_to_become_guide_benefit_2')}
                </span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: '12px',
                color: '#FFFFFF'
              }}>
                <span style={{ fontSize: '20px', marginRight: '12px' }}>🗓️</span>
                <span style={{ fontSize: '16px', lineHeight: '1.5' }}>
                  {I18n.t('ready_to_become_guide_benefit_3')}
                </span>
              </div>
            </div>

            {/* Youth Community Label */}
            <label style={{ 
              display: 'block', 
              color: '#FFFFFF', 
              fontSize: '16px', 
              marginBottom: '12px',
              fontWeight: '500'
            }}>
              {I18n.t('youth_community_question')} <small className="error" style={{ color: '#FF445D', marginLeft: '4px' }}>*</small>
            </label>

            {/* Youth Community Select */}
            <select
              ref={this.youthCommunitySelectRef}
              value={(() => {
                // Use modalYouthCommunity from state first
                const modalValue = this.state.modalYouthCommunity;
                if (modalValue) {
                  // If modalYouthCommunity is object, extract _id or value
                  if (typeof modalValue === 'object' && modalValue !== null) {
                    const extracted = modalValue._id || modalValue.value;
                    return extracted || '';
                  }
                  // If it's a string, return it directly
                  if (typeof modalValue === 'string') {
                    return modalValue;
                  }
                }
                // If not set, try to find from user.youthCommunity
                if (this.props.user?.youthCommunity && this.state.listBuddyYouthCommunity.length > 0) {
                  // Extract ID/value from user.youthCommunity
                  let youthCommunityToFind = null;
                  if (typeof this.props.user.youthCommunity === 'string') {
                    youthCommunityToFind = this.props.user.youthCommunity;
                  } else if (typeof this.props.user.youthCommunity === 'object') {
                    youthCommunityToFind = this.props.user.youthCommunity._id || this.props.user.youthCommunity.value;
                  }
                  
                  if (youthCommunityToFind) {
                    const foundById = this.state.listBuddyYouthCommunity.find(c => 
                      c._id === youthCommunityToFind || 
                      (c._id && c._id.toString() === youthCommunityToFind.toString())
                    );
                    if (foundById) {
                      return foundById._id;
                    }
                    const foundByValue = this.state.listBuddyYouthCommunity.find(c => 
                      c.value === youthCommunityToFind || 
                      (c.value && c.value.toString() === youthCommunityToFind.toString())
                    );
                    if (foundByValue) {
                      return foundByValue._id;
                    }
                  }
                }
                return '';
              })()}
              onChange={(e) => {
                const selectedValue = e.target.value;
                // Only update if a valid value is selected (not the placeholder)
                if (selectedValue && selectedValue !== '') {
                  this.handleModalSelectCommunity(selectedValue);
                } else {
                  // Clear the selection
                  this.handleModalSelectCommunity('');
                }
              }}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '16px',
                color: '#FFFFFF',
                backgroundColor: '#282F39',
                border: '1px solid #374151',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                marginBottom: '32px'
              }}
            >
              <option value="">{I18n.t('select_youth_community')}</option>
              {this.state.listBuddyYouthCommunity.map((item, index) => {
                if (index === 0 && item.value === '') return null; // Skip placeholder
                // Ensure we have a valid value for the option
                // Prefer _id, fall back to value, then use a temp value
                const optionValue = item._id || item.value;
                if (!optionValue || optionValue === '') return null; // Skip if no valid value
                return (
                  <option key={item._id || item.value || index} value={optionValue}>
                    {item.label || item.value || `Community ${index}`}
                  </option>
                );
              })}
            </select>

            {/* Buttons - equal width on one row */}
            <div style={{
              display: 'flex',
              gap: '20px',
              justifyContent: 'center',
              alignItems: 'stretch',
              flexWrap: 'nowrap'
            }}>
              {/* Maybe Later Button */}
              <button
                type="button"
                onClick={this.closeGuideModal}
                style={{
                  backgroundColor: 'transparent',
                  color: '#1AEEC2',
                  border: '1px solid #1AEEC2',
                  borderRadius: '16px',
                  padding: '12px 20px',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '0',
                  flex: '1 1 0',
                  whiteSpace: 'normal',
                  textAlign: 'center'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(55, 65, 81, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {I18n.t('maybe_later')}
              </button>

              {/* Become a Guide Button */}
              <button
                type="button"
                onClick={this.handleBecomeGuide}
                disabled={isLoading}
                style={{
                  backgroundColor: '#1AEEC2',
                  color: '#000',
                  border: 'none',
                  borderRadius: '16px',
                  padding: '12px 20px',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '0',
                  flex: '1 1 0',
                  opacity: isLoading ? 0.6 : 1,
                  whiteSpace: 'normal',
                  textAlign: 'center'
                }}
                onMouseOver={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.backgroundColor = '#16EAB1';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.backgroundColor = '#1AEEC2';
                  }
                }}
              >
                {isLoading ? I18n.t('processing') : I18n.t('become_a_guide')}
              </button>
            </div>
          </div>
        </Modal>

        <style>{`
          .disable-guide-modal .modal-content {
            background-color: transparent;
            border: none;
            box-shadow: none;
          }
        `}</style>
        <Modal
          className="disable-guide-modal"
          visible={this.state.showDisableGuideModal}
          dialogClassName="modal-dialog-centered"
          onClickBackdrop={this.closeDisableGuideModal}
        >
          <div
            className="modal-body"
            data-testid="disable-guide-modal"
            style={{
              padding: '40px',
              backgroundColor: '#1F2937',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '100%',
              textAlign: 'center',
            }}
          >
            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'center' }}>
              <img
                src={pauseRoleImage}
                alt=""
                style={{ maxWidth: '200px', height: 'auto' }}
              />
            </div>

            <h4
              style={{
                color: '#FFFFFF',
                fontSize: '24px',
                fontWeight: '600',
                marginBottom: '16px',
                lineHeight: '1.4',
              }}
            >
              {I18n.t('role_disable_guide_modal_title')}
            </h4>

            <p
              style={{
                color: '#FFFFFF',
                fontSize: '16px',
                lineHeight: '1.5',
                marginBottom: '32px',
              }}
            >
              {I18n.t('role_disable_guide_modal_description')}
            </p>

            <div
              style={{
                display: 'flex',
                gap: '20px',
                justifyContent: 'center',
                alignItems: 'stretch',
                flexWrap: 'nowrap',
              }}
            >
              <button
                type="button"
                data-testid="disable-guide-modal-maybe-later"
                onClick={this.closeDisableGuideModal}
                style={{
                  backgroundColor: 'transparent',
                  color: '#1AEEC2',
                  border: '1px solid #1AEEC2',
                  borderRadius: '16px',
                  padding: '12px 20px',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '0',
                  flex: '1 1 0',
                  whiteSpace: 'normal',
                  textAlign: 'center',
                }}
              >
                {I18n.t('maybe_later')}
              </button>

              <button
                type="button"
                data-testid="disable-guide-modal-confirm"
                onClick={this.confirmDisableGuide}
                disabled={isLoading}
                style={{
                  backgroundColor: '#1AEEC2',
                  color: '#000',
                  border: 'none',
                  borderRadius: '16px',
                  padding: '12px 20px',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '0',
                  flex: '1 1 0',
                  opacity: isLoading ? 0.6 : 1,
                  whiteSpace: 'normal',
                  textAlign: 'center',
                }}
              >
                {I18n.t('confirm')}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = ({ user }) => ({
  user,
  updating: user?.updating,
});

const mapDispatchToProps = (dispatch) => ({
  updateMvpUserType: (mvpUserType, youthCommunity) => dispatch(updateMvpUserType(mvpUserType, youthCommunity)),
  updateMvpUserRoleStatus: (action, role, youthCommunity) =>
    dispatch(updateMvpUserRoleStatus(action, role, youthCommunity)),
  getBuddyYouthCommunity: (options) => dispatch(getBuddyYouthCommunity(options)),
  fetchUserMvp: (callback) => dispatch(fetchUserMvp(callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RoleSelection);

